import { DoorOpen, Minus, Plus, Settings } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  AdminBulkActionRow,
  ConfirmDialog,
  EmptyState,
  IconButton,
  Modal,
  Pill,
  Switch,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import {
  ADMIN_ACTION_BUTTON,
  BUTTON_PRIMARY,
  FIELD_INPUT,
} from '@/shared/ui-class';
import { SEED_PENDING_SIGNUPS } from '@/versions/v1/shared/admin-seed';
import {
  AdminSceneShell,
  formatAdminDate,
} from './admin-shared';
import {
  allOnPageSelected,
  BULK_ADMIT_MAX,
  DEFAULT_LOBBY_SETTINGS,
  LOBBY_PANEL_SURFACES,
  parseDomainsInput,
  toggleSelectAllOnPage,
  toggleSignupSelection,
  type LobbyPanel,
  type LobbySettings,
  type SignupRow,
} from './SignupsScene.transform';

const CHECKBOX_CLASS =
  'h-4 w-4 rounded border-zinc-300 text-[var(--accent)] focus:ring-[var(--accent)] dark:border-zinc-600 dark:bg-zinc-800';

export function SignupsScene() {
  const [signups, setSignups] = useState<SignupRow[]>(() =>
    SEED_PENDING_SIGNUPS.map((signup) => ({ ...signup, disposition: 'pending' })),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<SignupRow | null>(null);
  const [settings, setSettings] = useState<LobbySettings>(DEFAULT_LOBBY_SETTINGS);
  const [draftSettings, setDraftSettings] = useState<LobbySettings>(settings);

  const pageIds = useMemo(() => signups.map((signup) => signup.id), [signups]);
  const allSelected = allOnPageSelected(selected, signups);
  const selectedPendingCount = useMemo(
    () =>
      signups.filter(
        (signup) => selected.has(signup.id) && signup.disposition === 'pending',
      ).length,
    [selected, signups],
  );
  const overBulkLimit = selectedPendingCount > BULK_ADMIT_MAX;

  const admitOne = (signupId: string) => {
    setSignups((prev) => prev.filter((signup) => signup.id !== signupId));
    setSelected((prev) => toggleSignupSelection(prev, signupId, false));
  };

  const rejectOne = (signupId: string) => {
    setSignups((prev) =>
      prev.map((signup) =>
        signup.id === signupId ? { ...signup, disposition: 'rejected' } : signup,
      ),
    );
    setSelected((prev) => toggleSignupSelection(prev, signupId, false));
  };

  const bulkAdmit = () => {
    if (overBulkLimit) return;
    setSignups((prev) =>
      prev.filter(
        (signup) =>
          !(selected.has(signup.id) && signup.disposition === 'pending'),
      ),
    );
    setSelected(new Set());
  };

  const openSettings = () => {
    setDraftSettings(settings);
    setSettingsOpen(true);
  };

  const saveSettings = () => {
    const normalizedDomains = parseDomainsInput(draftSettings.allowedDomains).join(', ');
    setSettings({ ...draftSettings, allowedDomains: normalizedDomains });
    setDraftSettings((prev) => ({ ...prev, allowedDomains: normalizedDomains }));
    setSettingsOpen(false);
  };

  const pendingCount = signups.filter((signup) => signup.disposition === 'pending').length;

  const updatePanel = (index: number, patch: Partial<LobbyPanel>) => {
    setDraftSettings((prev) => ({
      ...prev,
      panels: prev.panels.map((panel, i) => (i === index ? { ...panel, ...patch } : panel)),
    }));
  };

  const addPanel = () => {
    if (draftSettings.panels.length >= 6) return;
    setDraftSettings((prev) => ({
      ...prev,
      panels: [
        ...prev.panels,
        { title: '', body: '', surface: 'generic' as const },
      ],
    }));
  };

  const removePanel = (index: number) => {
    setDraftSettings((prev) => ({
      ...prev,
      panels: prev.panels.filter((_, i) => i !== index),
    }));
  };

  return (
    <AdminSceneShell
      sceneName="SignupsScene"
      title="Signup lobby"
      description="New signups wait here for admin approval. Admit or reject the queue."
      stackClassName="space-y-4"
    >
      <Commentable name="Toolbar">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              Lobby mode
            </span>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(enabled) => setSettings((prev) => ({ ...prev, enabled }))}
              aria-label="Lobby mode"
            />
          </div>
          <button
            type="button"
            onClick={openSettings}
            className={cn(ADMIN_ACTION_BUTTON, 'inline-flex items-center gap-1.5')}
          >
            <Settings className="h-3.5 w-3.5" aria-hidden />
            Settings
          </button>
        </div>
      </Commentable>

      <Commentable name="BulkBar">
        <div>
          <AdminBulkActionRow
            left={
              <span className="flex flex-wrap items-center gap-2">
                <Pill>{pendingCount}</Pill>
                {selected.size > 0 ? (
                  <span>{selected.size} selected</span>
                ) : (
                  <span>pending</span>
                )}
                {overBulkLimit ? (
                  <span className="text-amber-600 dark:text-amber-400">
                    Max {BULK_ADMIT_MAX} per bulk admit
                  </span>
                ) : null}
              </span>
            }
          >
            {selected.size > 0 ? (
              <button
                type="button"
                onClick={bulkAdmit}
                disabled={overBulkLimit}
                className={ADMIN_ACTION_BUTTON}
              >
                Admit selected ({selectedPendingCount})
              </button>
            ) : null}
          </AdminBulkActionRow>
        </div>
      </Commentable>

      <Commentable name="Table">
        <div>
          {signups.length === 0 ? (
            <EmptyState
              embedded
              icon={<DoorOpen className="h-8 w-8 text-zinc-400" />}
              title="No pending signups"
              description="New signups appear here while lobby mode is on."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH className="w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() =>
                        setSelected((prev) =>
                          toggleSelectAllOnPage(prev, pageIds, !allSelected),
                        )
                      }
                      aria-label="Select all on page"
                      className={CHECKBOX_CLASS}
                    />
                  </TH>
                  <TH>Email</TH>
                  <TH>Name</TH>
                  <TH>Signed up</TH>
                  <TH className="text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {signups.map((signup) => (
                  <TR
                    key={signup.id}
                    className={
                      signup.disposition === 'rejected' ? 'opacity-60' : undefined
                    }
                  >
                    <TD>
                      <input
                        type="checkbox"
                        checked={selected.has(signup.id)}
                        disabled={signup.disposition === 'rejected'}
                        onChange={(event) =>
                          setSelected((prev) =>
                            toggleSignupSelection(
                              prev,
                              signup.id,
                              event.target.checked,
                            ),
                          )
                        }
                        aria-label={`Select ${signup.email}`}
                        className={CHECKBOX_CLASS}
                      />
                    </TD>
                    <TD>{signup.email}</TD>
                    <TD>{signup.name ?? '—'}</TD>
                    <TD>{formatAdminDate(signup.createdAt)}</TD>
                    <TD className="text-right">
                      {signup.disposition === 'rejected' ? (
                        <Pill tone="danger">Rejected</Pill>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => admitOne(signup.id)}
                            className={ADMIN_ACTION_BUTTON}
                          >
                            Admit
                          </button>
                          <button
                            type="button"
                            onClick={() => setRejectTarget(signup)}
                            className={cn(
                              ADMIN_ACTION_BUTTON,
                              'text-red-600 dark:text-red-400',
                            )}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      </Commentable>

      <Modal open={settingsOpen} onOpenChange={setSettingsOpen} title="Lobby settings">
        <div className="space-y-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Contact email
            </span>
            <input
              type="email"
              value={draftSettings.contactEmail}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  contactEmail: event.target.value,
                }))
              }
              className={FIELD_INPUT}
              placeholder="admin@example.com (optional)"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Allowed domains
            </span>
            <input
              value={draftSettings.allowedDomains}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  allowedDomains: event.target.value,
                }))
              }
              className={FIELD_INPUT}
              placeholder="example.com, example.org (empty = all)"
            />
          </label>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Welcome headline
            </span>
            <input
              value={draftSettings.welcomeHeadline}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  welcomeHeadline: event.target.value,
                }))
              }
              className={FIELD_INPUT}
              placeholder="Welcome to our platform (optional)"
              maxLength={80}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Welcome subline
            </span>
            <textarea
              value={draftSettings.welcomeSubline}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  welcomeSubline: event.target.value,
                }))
              }
              rows={2}
              className={FIELD_INPUT}
              placeholder="A short description shown below the headline (optional)"
              maxLength={160}
            />
          </label>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                Panels ({draftSettings.panels.length}/6)
              </span>
              <IconButton
                label="Add panel"
                onClick={addPanel}
                disabled={draftSettings.panels.length >= 6}
              >
                <Plus className="h-3.5 w-3.5" />
              </IconButton>
            </div>
            {draftSettings.panels.map((panel, index) => (
              <div
                key={`panel-${index}`}
                className="space-y-2 rounded-lg border border-zinc-200 p-3 dark:border-zinc-700"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted">Panel {index + 1}</span>
                  <IconButton label="Remove panel" onClick={() => removePanel(index)}>
                    <Minus className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
                <input
                  value={panel.title}
                  onChange={(event) => updatePanel(index, { title: event.target.value })}
                  className={FIELD_INPUT}
                  placeholder="Panel title"
                />
                <textarea
                  value={panel.body}
                  onChange={(event) => updatePanel(index, { body: event.target.value })}
                  rows={2}
                  className={FIELD_INPUT}
                  placeholder="Panel body"
                />
                <select
                  value={panel.surface}
                  onChange={(event) =>
                    updatePanel(index, {
                      surface: event.target.value as LobbyPanel['surface'],
                    })
                  }
                  className={FIELD_INPUT}
                >
                  {LOBBY_PANEL_SURFACES.map((surface) => (
                    <option key={surface} value={surface}>
                      {surface}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <button type="button" onClick={saveSettings} className={BUTTON_PRIMARY}>
            Save
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={rejectTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRejectTarget(null);
        }}
        title="Reject signup"
        description={
          rejectTarget ? (
            <>
              This deletes the signup for <strong>{rejectTarget.email}</strong>. They
              can sign up again later.
            </>
          ) : null
        }
        confirmLabel="Reject"
        tone="danger"
        onConfirm={() => {
          if (rejectTarget) rejectOne(rejectTarget.id);
        }}
      />
    </AdminSceneShell>
  );
}
